import React, { useEffect, useState, useCallback } from "react";
import Layout from "./Layout";
import axios, { cookies, MEDIA_BASE_URL } from "../../axios/axios";
import { ToastContainer, toast } from "react-toastify";
import Navbar from "../../Components/Navbar";
import Footer from "../../Components/Footer";
import PaginationControls from "../../Components/PaginationControls";
import RowsPerPageSelect from "../../Components/RowsPerPageSelect";
import ExportCsvButton from "../../Components/ExportCsvButton";
import DateRangeFilter from "../../Components/DateRangeFilter";
import downloadImagesAsZip from "../../utils/downloadImagesAsZip";

const AllTrials = () => {
  const [trials, setTrials] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const getTrials = useCallback(async () => {
    try {
      const response = await axios.get("/trial/", {
        params: {
          page: currentPage,
          limit: rowsPerPage,
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        },
      });
      setTrials(response.data.results);
      setTotalPages(response.data.totalPages);
      setTotalResults(response.data.totalResults);
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    }
  }, [currentPage, rowsPerPage, startDate, endDate]);

  useEffect(() => {
    getTrials();
  }, [getTrials]);

  const user = cookies.get("user");

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setCurrentPage(1);
  };

  const fetchAllForExport = useCallback(async () => {
    const response = await axios.get("/trial/", {
      params: {
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      },
    });
    return response.data;
  }, [startDate, endDate]);

  const handleStartDateChange = (value) => {
    setStartDate(value);
    setCurrentPage(1);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleDownloadZip = async (trial) => {
    if (!trial.images || trial.images.length === 0) return;
    setDownloadingId(trial.id);
    try {
      const urls = trial.images.map((image) => `${MEDIA_BASE_URL}/${image}`);
      await downloadImagesAsZip(urls, `trial-${trial.id}-images.zip`);
    } catch (error) {
      toast.error("Failed to download images.");
    } finally {
      setDownloadingId(null);
    }
  };

  const csvColumns = [
    {
      label: "Name",
      accessor: (row) =>
        [row?.userId?.fname, row?.userId?.lname].filter(Boolean).join(" "),
    },
    { label: "Email", accessor: (row) => row?.userId?.email },
    { label: "Order Title", accessor: (row) => row.title },
    { label: "Order Service", accessor: (row) => row.service },
    { label: "Description", accessor: (row) => row.description },
    {
      label: "Date",
      accessor: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  return (
    <>
      <Navbar />
      <Layout>
        <div className="pe-5 bg-light h-100">
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <div className="bg-light">
            <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-2">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onClear={handleClearDates}
              />
              <div className="d-flex align-items-center gap-3">
                <ExportCsvButton
                  filename="trials.csv"
                  columns={csvColumns}
                  fetchRows={fetchAllForExport}
                />
                <RowsPerPageSelect
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </div>
            </div>
            <table className="table table-container table-light">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col " className="fs-14">
                    Name
                  </th>{" "}
                  <th scope="col " className="fs-14">
                    Email
                  </th>{" "}
                  <th scope="col " className="fs-14">
                    Order Title
                  </th>{" "}
                  <th scope="col " className="fs-14">
                    Order Service
                  </th>{" "}
                  <th scope="col " className="fs-14">
                    Description
                  </th>{" "}
                  <th scope="col " className="fs-14">
                    Image
                  </th>
                  <th scope="col " className="fs-14">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {trials.map((val, index) => (
                  <tr key={index}>
                    <th scope="row">
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </th>
                    <td className="fs-14">
                      {val?.userId?.fname + " " + val?.userId?.lname}
                    </td>
                    <td className="fs-14">{val?.userId?.email}</td>
                    <td className="fs-14">{val.title}</td>
                    <td className="fs-14">{val.service}</td>
                    <td className="fs-14">{val.description}</td>
                    <td className="fs-14">
                      {val.images && val.images.length > 0 ? (
                        <div
                          className="d-flex flex-wrap gap-2"
                          role="button"
                          title="Click to download all photos as a ZIP"
                          style={{
                            cursor: "pointer",
                            opacity: downloadingId === val.id ? 0.5 : 1,
                          }}
                          onClick={() => handleDownloadZip(val)}
                        >
                          {val.images.map((image, idx) => (
                            <img
                              key={idx}
                              src={`${MEDIA_BASE_URL}/${image}`}
                              crossOrigin="anonymous"
                              alt={`Trial Image ${idx + 1}`}
                              width={150}
                              height={100}
                              className="rounded object-fit-cover"
                            />
                          ))}
                        </div>
                      ) : (
                        "No Images"
                      )}
                    </td>
                    <td className="fs-14">
                      {new Date(val.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalResults={totalResults}
              onPageChange={(page) =>
                setCurrentPage(Math.min(Math.max(page, 1), totalPages || 1))
              }
            />
          </div>
        </div>
      </Layout>
      <Footer />
    </>
  );
};

export default AllTrials;
